/**
 * ============================================================================
 * EVOLIS — module assistant IA (développement Louis, conçu pour être détachable)
 *  Contexte de navigation : associe la route courante à un libellé lisible
 *  (transmis au prompt pour qu'Evolis sache où se trouve l'utilisateur) et à
 *  des suggestions de questions adaptées à la page + au profil (encadrement /
 *  pages du rôle). Voir evolu-backend/src/Evolis/docs/REGLES-PORTEE.md.
 * ============================================================================
 */

import authService from '../services/auth.service';

/** Suggestions génériques « vision entité » (stats / CA). */
export const SUGGESTIONS_ENTITE = [
    'Quel est le CA des projets signés cette année ?',
    'Répartition par genre de provenance des projets signés cette année',
    'Combien de projets en cours en ce moment ?',
    'Quelles sont les raisons de perte des projets cette année ?',
];

/** Suggestions pour un employé (assistant perso). */
export const SUGGESTIONS_PERSONNEL = [
    'Fais-moi le point du jour',
    'Quelles sont mes tâches de la semaine ?',
    'Liste mes projets en cours avec leur budget',
];

/** @deprecated alias — préférer filtrerSuggestions() */
export const SUGGESTIONS_GENERIQUES = SUGGESTIONS_ENTITE;

const PAGES = [
    {
        prefixe: '/admin/events',
        label: 'le Planning (calendrier des salons)',
        domaine: 'salons',
        suggestions: [
            'Quels salons ont lieu ce mois-ci ?',
            'Quels montages commencent bientôt ?',
            'Quels salons arrivent le mois prochain ?',
        ],
    },
    {
        prefixe: '/admin/equipe-planning',
        label: 'Équipe & planning (son planning personnel)',
        domaine: 'planning_perso',
        suggestions: [
            'Fais-moi le point du jour',
            'Quelles sont mes tâches de la semaine ?',
            'Qui est absent cette semaine ?',
        ],
        suggestionsPerso: [
            'Fais-moi le point du jour',
            'Quelles sont mes tâches de la semaine ?',
            'Liste mes projets en cours avec leur budget',
        ],
    },
    {
        prefixe: '/admin/planning-manager',
        label: "le Planning d'assignation de l'équipe studio",
        domaine: 'planning_equipe',
        suggestions: [
            "Quelle est la charge de l'équipe cette semaine ?",
            'Y a-t-il des tâches en retard ?',
            'Qui est absent cette semaine ?',
        ],
    },
    {
        prefixe: '/admin/salons',
        label: 'la page Salons',
        domaine: 'salons',
        suggestions: [
            'Quels salons arrivent bientôt ?',
            'Quels salons ont lieu ce mois-ci ?',
            'Combien de projets sur chaque salon des 90 prochains jours ?',
        ],
    },
    {
        prefixe: '/admin/projets-parametres',
        label: 'les Paramètres Projets',
        domaine: 'stats',
        suggestions: SUGGESTIONS_ENTITE,
        suggestionsPerso: SUGGESTIONS_PERSONNEL,
    },
    {
        prefixe: '/admin/projets',
        label: 'la page Projets',
        domaine: 'projets',
        suggestions: [
            'Combien de projets en cours en ce moment ?',
            'Liste les projets signés cette année',
            'Quelles sont les raisons de perte des projets cette année ?',
        ],
        suggestionsPerso: [
            'Liste mes projets en cours avec leur budget',
            'Combien de mes projets sont en cours ?',
            'Fais-moi le point du jour',
        ],
    },
    {
        prefixe: '/admin/clients',
        label: 'la page Clients',
        domaine: 'projets',
        suggestions: [
            'Liste les projets signés cette année avec leurs clients',
            'Combien de projets en cours en ce moment ?',
            'Répartition par genre de provenance des projets signés cette année',
        ],
        suggestionsPerso: [
            'Liste mes projets en cours avec leur budget',
            'Fais-moi le point du jour',
        ],
    },
    {
        prefixe: '/admin/statistiques',
        label: 'la page Statistiques',
        domaine: 'stats',
        suggestions: [
            'Quel est le CA des projets signés cette année ?',
            'Nombre de projets signés et perdus cette année, par statut',
            'Répartition par genre de provenance des projets signés cette année',
        ],
    },
];

const ACCUEIL = {
    label: 'le Tableau de bord',
    suggestions: [
        'Fais-moi le point du jour',
        'Combien de projets en cours en ce moment ?',
        'Quels salons arrivent bientôt ?',
    ],
    suggestionsPerso: SUGGESTIONS_PERSONNEL,
};

const DEFAUT = { label: null, suggestions: SUGGESTIONS_ENTITE, suggestionsPerso: SUGGESTIONS_PERSONNEL };

/** Profil côté front (miroir simplifié de EvolisPortee). */
export function profilEvolis() {
    const user = authService.getCurrentUser();
    const a = (p) => authService.hasPermission(p);
    const encadrement = !!(user?.fonctionEncadrement);
    const niveauEntite = encadrement || a('access_statistiques');
    return {
        niveauEntite,
        encadrement,
        peutSalons: a('access_salons') || a('access_events'),
        peutProjets: a('access_projets') || a('access_statistiques') || encadrement,
        peutStats: a('access_statistiques') || encadrement || a('access_projets'),
        peutPlanningEquipe: a('access_planning_manager') || encadrement,
        peutPlanningPerso: a('access_equipe_planning') || a('access_planning_manager') || encadrement,
    };
}

function pageBrute(pathname) {
    if (!pathname || pathname === '/') return ACCUEIL;
    return PAGES.find((p) => pathname.startsWith(p.prefixe)) || DEFAUT;
}

/**
 * Page courante + suggestions filtrées selon le profil utilisateur.
 */
export function pageCourante(pathname) {
    const page = pageBrute(pathname);
    const profil = profilEvolis();
    let suggestions = (!profil.niveauEntite && page.suggestionsPerso)
        ? page.suggestionsPerso
        : page.suggestions;

    // Retire les suggestions hors domaines (ex. salons sans access_salons).
    suggestions = suggestions.filter((s) => {
        const low = s.toLowerCase();
        if ((low.includes('salon') || low.includes('montage')) && !profil.peutSalons) return false;
        if ((low.includes('ca ') || low.includes('provenance') || low.includes('perte')) && !profil.niveauEntite && !profil.peutStats) return false;
        if ((low.includes('charge') || low.includes('absent') || low.includes('retard')) && !profil.peutPlanningEquipe) {
            // « mes tâches » / point du jour restent OK
            if (low.includes('mes tâches') || low.includes('point du jour')) return true;
            return false;
        }
        return true;
    });

    if (suggestions.length === 0) {
        suggestions = profil.niveauEntite ? SUGGESTIONS_ENTITE.slice(0, 3) : SUGGESTIONS_PERSONNEL;
    }

    return { label: page.label, suggestions };
}
